import { useEffect, useRef } from 'react';
import { useAppState } from '../state/AppState';
import { saveSession } from '../services/sessionService';
import { getConfig } from '../services/configService';
import { getAutoSaveDebounceMs } from '../config/sessionConfig';

/**
 * Global session saving hook that automatically saves state changes.
 * 
 * This hook monitors the app state and automatically saves to the backend
 * whenever relevant state changes occur, excluding ScreenP form data.
 */
export function useGlobalSessionSave() {
  const { state } = useAppState();
  const lastSavedState = useRef<string>('');
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Allow session saving as soon as there's meaningful state to save
    // Don't wait for initial data loading to complete

    // Don't auto-save if we have no meaningful state (all null/empty values)
    const hasAnyState = state.visitType || state.location || state.service || state.reason || 
                       state.insurance || state.appointmentDate || state.appointmentTime ||
                       state.stateString || state.ageGroup ||
                       state.profileId || state.columnId || state.licenseKey || state.availableLocations;
    if (!hasAnyState) {
      return;
    }

    // Create a hash of the current state (excluding form data from ScreenP)
    const stateToSave = {
      providerId: state.providerId,
      visitType: state.visitType,
      location: state.location,
      locationCode: state.locationCode,
      locationAddress: state.locationAddress,
      service: state.service,
      reason: state.reason,
      insurance: state.insurance,
      appointmentDate: state.appointmentDate,
      appointmentTime: state.appointmentTime,
      stateString: state.stateString,
      profileId: state.profileId,
      columnId: state.columnId,
      licenseKey: state.licenseKey,
      ageGroup: state.ageGroup,
      existingClient: state.existingClient,
      availableLocations: state.availableLocations,
      selectedAvailability: state.selectedAvailability,
      reservationStartTime: state.reservationStartTime
    };

    const currentStateHash = JSON.stringify(stateToSave);

    // Only save if state has actually changed
    if (currentStateHash !== lastSavedState.current) {
      // Clear any existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Debounce saves to avoid too many API calls
      saveTimeoutRef.current = setTimeout(async () => {
        try {
          const config = getConfig();
          const remoteApiUrl = config.remoteApiUrl || 'http://localhost:5293';
          
          if (!remoteApiUrl) {
            return;
          }
          
          await saveSession(state, remoteApiUrl, 'Global auto-save');
          lastSavedState.current = currentStateHash;
        } catch (error) {
        }
      }, getAutoSaveDebounceMs()); // Configurable debounce timeout
    }

    // Cleanup timeout on unmount
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [
    state.sessionData,
    state.availabilities,
    state.providerId,
    state.visitType,
    state.location,
    state.locationCode,
    state.locationAddress,
    state.service,
    state.reason,
    state.insurance,
    state.appointmentDate,
    state.appointmentTime,
    state.stateString,
    state.profileId,
    state.columnId,
    state.licenseKey,
    state.ageGroup,
    state.existingClient,
    state.availableLocations,
    state.selectedAvailability,
    state.reservationStartTime,
    state.remoteApiUrl
  ]);
}
